/**
 * Composable that scrolls a dynamically-loaded element into view and briefly
 * emphasizes it once visible. Designed for "deep-link" flows where the target
 * may not exist in the DOM at mount time (e.g. navigating to a specific
 * verbatim excerpt in a word-cloud stream).
 *
 * Flow: setTarget(el) → scroll into view → wait for visibility + scroll idle → emphasize → auto-reset.
 *
 * All watchers and timers are one-shot and self-cleaning via scope disposal.
 *
 * @example
 * ```ts
 * const { setTarget, isEmphasized } = useLoadAndScroll();
 *
 * // In a v-for or async render callback:
 * if (item.id === highlightId) setTarget(el);
 *
 * // Bind emphasis class:
 * :class="{ 'emphasized': isEmphasized }"
 * ```
 */
import { ref, watch, nextTick, computed } from "vue";
import { useScroll, useIntersectionObserver, useTimeout } from "@vueuse/core";
const resolveElement = (val) => val ? ("$el" in val ? val.$el : val) : null;
export const useLoadAndScroll = (options) => {
    const { scrollBehavior = "smooth", scrollBlock = "center", intersectionThreshold = 0.35, emphasizeDuration = 700 } = options ?? {};
    const target = ref(null);
    const hasScrolled = ref(false);
    const hasEmphasized = ref(false);
    const isTargetInView = ref(false);
    const { ready: emphasisDone, start: startEmphasisTimer } = useTimeout(emphasizeDuration, {
        controls: true,
        immediate: false
    });
    const isEmphasized = computed(() => hasEmphasized.value && !emphasisDone.value);
    const setTarget = (el) => {
        target.value = el;
    };
    // Scroll watcher — on first non-null target, scroll to it
    const stopScrollWatch = watch(target, (el) => {
        if (el && !hasScrolled.value) {
            hasScrolled.value = true;
            stopScrollWatch();
            nextTick(() => {
                resolveElement(el)?.scrollIntoView({ behavior: scrollBehavior, block: scrollBlock });
            });
        }
    });
    // Visibility tracking
    const { stop: stopIntersectionObserver } = useIntersectionObserver(computed(() => resolveElement(target.value)), ([entry]) => {
        isTargetInView.value = entry.isIntersecting;
    }, { threshold: intersectionThreshold });
    // Scroll state
    const { isScrolling } = useScroll(window);
    // Emphasis gate — fires once when scroll is done, target is visible, and user stopped scrolling
    const stopEmphasisWatch = watch(() => hasScrolled.value && isTargetInView.value && !isScrolling.value && !hasEmphasized.value, (ready) => {
        if (!ready)
            return;
        hasEmphasized.value = true;
        startEmphasisTimer();
        stopEmphasisWatch();
        stopIntersectionObserver();
    });
    return { setTarget, isEmphasized };
};
